/**
 * Schedule window evaluation for the flow `schedule` node.
 *
 * A window is the AND of the conditions it carries; the node matches when ANY
 * window matches. A condition the window does not carry does not constrain it,
 * so a window with only a date range matches that whole day, in any hour.
 *
 * Condition shapes (as the Flow Builder saves them in `scheduleConfig.windows`):
 *   { type: "time",     start: "09:00", end: "18:00" }
 *   { type: "weekdays", days: ["mon", "tue", ...] }
 *   { type: "date",     startDay, startMonth, startYear, endDay, endMonth, endYear }
 *
 * Date fields are strings with a 1-based month ("10" is October). An empty year
 * means the current year in the schedule timezone; an empty end means the range
 * is open-ended ("A partir de").
 *
 * Flows that never had `windows` (legacy `ranges[]` or a flat `{days,start,end}`)
 * keep their original semantics: 09:00-18:00 when no hours are set, every day
 * when no days are set.
 */

import { getZonedParts, DEFAULT_TIMEZONE } from "./zonedTime.js";

const WEEKDAY_IDS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const LEGACY_DEFAULT_START = "09:00";
const LEGACY_DEFAULT_END = "18:00";

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

/**
 * A time of day typed into the editor, as minutes since midnight, or NaN when it
 * is not one.
 *
 * The Horário inputs are free text, and people type the hour the way they would
 * write it: "16:00", but also "1600", "16h", "16h30" or "09h00". Strict "HH:MM"
 * parsing turned those into NaN, the window never matched and the node always
 * took "Fora do horário" (Magalhães Rates, 2026-10-22: end "1600" answered
 * "funciona das 09h às 16h" at 14:55). "24:00" is accepted as the end of the day.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function parseTimeOfDay(value) {
  const text = String(value ?? "").trim().toLowerCase();
  const match =
    /^(\d{1,2})[:h](\d{2})$/.exec(text) || // 16:00, 9:30, 16h30
    /^(\d{1,2})(\d{2})$/.exec(text) || // 1600, 930
    /^(\d{1,2})h?$/.exec(text); // 16, 16h
  if (!match) return NaN;

  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  if (minutes > 59) return NaN;
  if (hours > 24 || (hours === 24 && minutes > 0)) return NaN;
  return hours * 60 + minutes;
}

/**
 * The editor's canonical "HH:MM" for a typed time of day, or null when it is not
 * one.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export function normalizeTimeOfDay(value) {
  const minutes = parseTimeOfDay(value);
  if (Number.isNaN(minutes)) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** A `time` condition's bound in minutes; a blank one is the editor default. */
function timeBound(value, fallback) {
  return parseTimeOfDay(isBlank(value) ? fallback : value);
}

/**
 * One side of a date range as a sortable `YYYYMMDD` integer.
 *
 * @returns {{status: "empty"} | {status: "invalid"} | {status: "ok", value: number}}
 */
function toYmd(day, month, year, currentYear) {
  if (isBlank(day) && isBlank(month)) return { status: "empty" };
  if (isBlank(day) || isBlank(month)) return { status: "invalid" };

  const d = Number(day);
  const m = Number(month);
  const y = isBlank(year) ? currentYear : Number(year);
  if (![d, m, y].every(Number.isInteger)) return { status: "invalid" };
  if (m < 1 || m > 12 || y < 1900 || y > 9999) return { status: "invalid" };

  // Day 0 of the next month is the last day of this one (handles leap years).
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d < 1 || d > daysInMonth) return { status: "invalid" };

  return { status: "ok", value: y * 10000 + m * 100 + d };
}

function matchesCondition(condition, now) {
  switch (condition?.type) {
    case "time": {
      // A cleared input falls back to the editor's defaults, as the saved
      // `ranges` mirror always did.
      const start = timeBound(condition.start, LEGACY_DEFAULT_START);
      const end = timeBound(condition.end, LEGACY_DEFAULT_END);
      if (Number.isNaN(start) || Number.isNaN(end)) return false;
      const current = now.hour * 60 + now.minute;
      return current >= start && current < end;
    }

    case "weekdays": {
      const days = Array.isArray(condition.days) ? condition.days : [];
      return days.length === 0 || days.includes(WEEKDAY_IDS[now.weekday]);
    }

    case "date": {
      const start = toYmd(
        condition.startDay,
        condition.startMonth,
        condition.startYear,
        now.year,
      );
      // Without a valid start there is no range to be inside of. Failing closed
      // here is deliberate: a half-filled date must not read as "always".
      if (start.status !== "ok") return false;

      const end = toYmd(
        condition.endDay,
        condition.endMonth,
        condition.endYear,
        now.year,
      );
      if (end.status === "invalid") return false;

      const today = now.year * 10000 + now.month * 100 + now.day;
      if (today < start.value) return false;
      return end.status === "empty" || today <= end.value;
    }

    default:
      // Unknown condition type (e.g. written by a newer editor): do not guess.
      return false;
  }
}

/**
 * Editor-side check of a `date` condition.
 *
 * @param {object} condition
 * @param {number} [currentYear] year an empty year field stands for
 * @returns {"missing_start" | "invalid_start" | "invalid_end" | "end_before_start" | null}
 */
export function checkDateCondition(
  condition,
  currentYear = new Date().getFullYear(),
) {
  const start = toYmd(
    condition?.startDay,
    condition?.startMonth,
    condition?.startYear,
    currentYear,
  );
  if (start.status === "empty") return "missing_start";
  if (start.status === "invalid") return "invalid_start";

  const end = toYmd(
    condition?.endDay,
    condition?.endMonth,
    condition?.endYear,
    currentYear,
  );
  if (end.status === "invalid") return "invalid_end";
  if (end.status === "ok" && end.value < start.value) return "end_before_start";
  return null;
}

/**
 * Editor-side check of a `time` condition. A blank bound is not a problem: it
 * stands for the editor default (09:00 / 18:00).
 *
 * @param {object} condition
 * @returns {"invalid_start" | "invalid_end" | "end_not_after_start" | null}
 */
export function checkTimeCondition(condition) {
  const start = timeBound(condition?.start, LEGACY_DEFAULT_START);
  if (Number.isNaN(start)) return "invalid_start";
  const end = timeBound(condition?.end, LEGACY_DEFAULT_END);
  if (Number.isNaN(end)) return "invalid_end";
  // [start, end) on the same day: an overnight window never matches.
  if (end <= start) return "end_not_after_start";
  return null;
}

/** Legacy `ranges[]` / flat config rewritten as one-window-per-range conditions. */
function legacyWindows(scheduleConfig) {
  const ranges =
    Array.isArray(scheduleConfig.ranges) && scheduleConfig.ranges.length > 0
      ? scheduleConfig.ranges
      : [scheduleConfig];

  return ranges.map((range) => ({
    conditions: [
      {
        type: "time",
        start: range.start || LEGACY_DEFAULT_START,
        end: range.end || LEGACY_DEFAULT_END,
      },
      { type: "weekdays", days: range.days || [] },
    ],
  }));
}

/**
 * Whether `date` falls inside any window of a schedule config.
 *
 * @param {object} scheduleConfig `{ timezone?, windows?, ranges?, days?, start?, end? }`
 * @param {Date} [date]
 * @returns {{
 *   matched: boolean,
 *   matchedWindow: number | null,
 *   timezone: string,
 *   source: "windows" | "legacy",
 *   windows: Array<{ index: number, matched: boolean, conditions: Array<{ type: string, matched: boolean, problem?: string }> }>
 * }}
 */
export function evaluateScheduleWindows(scheduleConfig, date = new Date()) {
  const config = scheduleConfig || {};
  const timezone = config.timezone || DEFAULT_TIMEZONE;
  const now = getZonedParts(date, timezone);

  const hasWindows = Array.isArray(config.windows) && config.windows.length > 0;
  const windows = hasWindows ? config.windows : legacyWindows(config);

  const details = windows.map((window, index) => {
    const conditions = Array.isArray(window?.conditions) ? window.conditions : [];
    const results = conditions.map((condition) => {
      const result = {
        type: String(condition?.type),
        matched: matchesCondition(condition, now),
      };
      const problem =
        condition?.type === "time" ? checkTimeCondition(condition) : null;
      if (problem) result.problem = problem;
      return result;
    });
    // An empty window never matches: a blank window must not mean "always".
    const matched = results.length > 0 && results.every((r) => r.matched);
    return { index, matched, conditions: results };
  });

  const first = details.find((w) => w.matched);

  return {
    matched: Boolean(first),
    matchedWindow: first ? first.index : null,
    timezone,
    source: hasWindows ? "windows" : "legacy",
    windows: details,
  };
}
